import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiTarget, FiDatabase, FiSettings, FiAirplay, FiCheckCircle, FiAlertCircle, FiLoader, FiInbox } = FiIcons;
import { demoSteps, mockPlants, mockHarvestNotes, mockSupplies } from '../../data/mockData';

export default function DemoView() {
  const [activeStep, setActiveStep] = useState(1);
  const [simState, setSimState] = useState('success'); // success, error, empty, loading
  const [dataFilter, setDataFilter] = useState('all');

  const currentStepData = demoSteps.find(s => s.id === activeStep);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8 pb-24 md:pb-12 max-w-6xl mx-auto px-1 md:px-0"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-2 border-b border-garden-base pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <SafeIcon icon={FiTarget} className="text-lime text-xl" />
            <h2 className="text-2xl font-bold text-clay uppercase tracking-tight">Demo / How It Works</h2>
          </div>
          <p className="text-sm text-clay-muted">System protocol for urban bio-management and predictive gardening.</p>
        </div>
        <div className="flex items-center gap-2 bg-garden-dark px-3 py-1.5 rounded-lg border border-garden-base/50">
          <div className="w-2 h-2 rounded-full bg-lime animate-ping"></div>
          <span className="text-[10px] font-mono text-lime uppercase tracking-widest">Live Simulator v1.0.4</span>
        </div>
      </div>

      {/* Section: Try this flow */}
      <div className="space-y-4">
        <h3 className="text-xs font-mono text-lime uppercase tracking-[0.2em] flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-lime rounded-full"></span> Try this flow
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Step Selector */}
          <div className="lg:col-span-4 space-y-2">
            {demoSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center gap-4 ${
                  activeStep === step.id 
                    ? 'bg-garden-base border-lime text-lime shadow-[0_0_20px_rgba(163,230,53,0.1)]' 
                    : 'bg-garden-dark border-garden-base text-clay-muted hover:border-clay-muted/50'
                }`}
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono text-xs ${
                  activeStep === step.id ? 'bg-lime text-garden-darkest' : 'bg-garden-darkest border border-garden-base'
                }`}>
                  0{step.id}
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide">{step.title}</p>
                  <p className="text-[10px] opacity-60 font-mono">{step.module}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Interactive Simulation Panel */}
          <div className="lg:col-span-8">
            <div className="glass-panel rounded-2xl overflow-hidden flex flex-col h-full border border-garden-base">
              <div className="bg-garden-dark p-4 border-b border-garden-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/50"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/50"></div>
                </div>
                <div className="text-[10px] font-mono text-clay-dark uppercase">Terminal: {currentStepData.module}</div>
              </div>
              
              <div className="p-8 flex-1 flex flex-col items-center justify-center text-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    className="max-w-md"
                  >
                    <div className="w-16 h-16 bg-lime/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-lime/20">
                      <SafeIcon name={currentStepData.icon} className="text-3xl text-lime glow-text" />
                    </div>
                    <h4 className="text-xl font-bold text-clay mb-3">{currentStepData.title}</h4>
                    <p className="text-sm text-clay-muted leading-relaxed mb-8">
                      {currentStepData.desc}
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                      <button className="px-6 py-2.5 bg-lime text-garden-darkest rounded-lg font-bold text-sm hover:scale-105 transition-transform">
                        Execute {currentStepData.icon}
                      </button>
                      <button className="px-6 py-2.5 bg-garden-dark border border-garden-base rounded-lg text-sm text-clay hover:bg-garden-base transition-colors">
                        Protocol Details
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              
              <div className="bg-garden-darkest/50 p-4 flex items-center justify-between text-[10px] font-mono text-clay-dark border-t border-garden-base">
                <span>ENCRYPTED_STREAM_ID: GX-992</span>
                <span className="flex items-center gap-1">
                  <SafeIcon icon={FiAirplay} /> SYSTEM_READY
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section: Simulated States */}
      <div className="space-y-4">
        <h3 className="text-xs font-mono text-lime uppercase tracking-[0.2em] flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-lime rounded-full"></span> Simulated States
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {['success', 'error', 'empty', 'loading'].map(state => (
            <button
              key={state}
              onClick={() => setSimState(state)}
              className={`py-2 rounded-lg border text-[10px] font-mono uppercase tracking-widest transition-all ${
                simState === state 
                  ? 'bg-lime text-garden-darkest border-lime' 
                  : 'bg-garden-dark border-garden-base text-clay-muted hover:border-clay-muted/50'
              }`}
            >
              {state} state
            </button>
          ))}
        </div>

        <div className="glass-panel p-12 rounded-2xl flex items-center justify-center min-h-[300px] border border-garden-base relative overflow-hidden">
          <AnimatePresence mode="wait">
            {simState === 'loading' && (
              <motion.div key="load" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                <SafeIcon icon={FiLoader} className="text-4xl text-lime animate-spin" />
                <p className="text-xs font-mono text-lime uppercase tracking-widest">Calibrating sensors...</p>
              </motion.div>
            )}
            {simState === 'success' && (
              <motion.div key="succ" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-lime/20 flex items-center justify-center border border-lime/50">
                  <SafeIcon icon={FiCheckCircle} className="text-2xl text-lime" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-clay">Bio-Sync Complete</p>
                  <p className="text-[10px] text-clay-muted mt-1">All systems reporting optimal saturation.</p>
                </div>
              </motion.div>
            )}
            {simState === 'error' && (
              <motion.div key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/50">
                  <SafeIcon icon={FiAlertCircle} className="text-2xl text-red-500" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-red-500">Signal Interrupted</p>
                  <p className="text-[10px] text-clay-muted mt-1">External sensor array offline. Re-check connectivity.</p>
                </div>
              </motion.div>
            )}
            {simState === 'empty' && (
              <motion.div key="emp" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                <SafeIcon icon={FiInbox} className="text-4xl text-clay-dark" />
                <div className="text-center">
                  <p className="text-sm font-bold text-clay">Archive Vacant</p>
                  <p className="text-[10px] text-clay-muted mt-1">No data logs found for this cycle.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Section: Sample Data */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h3 className="text-xs font-mono text-lime uppercase tracking-[0.2em] flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-lime rounded-full"></span> Sample Data
          </h3>
          <div className="flex bg-garden-dark border border-garden-base rounded-lg p-1">
            {['all', 'plants', 'harvests', 'supplies'].map(f => (
              <button
                key={f}
                onClick={() => setDataFilter(f)}
                className={`px-3 py-1 rounded text-[10px] font-mono uppercase transition-colors ${
                  dataFilter === f ? 'bg-garden-base text-lime' : 'text-clay-muted'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(dataFilter === 'all' || dataFilter === 'plants') && mockPlants.map(plant => (
            <div key={plant.id} className="bg-garden-dark border border-garden-base rounded-xl p-4 flex gap-4 items-center">
              <img src={plant.image} className="w-12 h-12 rounded-lg object-cover grayscale brightness-75" alt="" />
              <div className="flex-1">
                <p className="text-xs font-bold text-clay">{plant.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1 bg-garden-darkest rounded-full overflow-hidden">
                    <div className="h-full bg-lime/60" style={{ width: `${plant.hydration}%` }}></div>
                  </div>
                  <span className="text-[10px] font-mono text-lime">{plant.hydration}%</span>
                </div>
              </div>
            </div>
          ))}
          {(dataFilter === 'all' || dataFilter === 'harvests') && mockHarvestNotes.map(note => (
            <div key={note.id} className="bg-garden-dark border border-garden-base rounded-xl p-4 flex gap-4 items-center">
              <div className="w-12 h-12 rounded-lg bg-garden-base flex items-center justify-center">
                <SafeIcon icon={FiDatabase} className="text-lime" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-clay">{note.plant} Log</p>
                <p className="text-[10px] text-clay-muted mt-0.5">Yield: {note.yield} • {note.quality}</p>
              </div>
            </div>
          ))}
           {(dataFilter === 'all' || dataFilter === 'supplies') && mockSupplies.map(supply => (
            <div key={supply.id} className="bg-garden-dark border border-garden-base rounded-xl p-4 flex gap-4 items-center">
              <div className="w-12 h-12 rounded-lg bg-garden-base flex items-center justify-center">
                <SafeIcon icon={FiSettings} className="text-clay-muted" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-clay">{supply.name}</p>
                <p className={`text-[10px] mt-0.5 ${supply.status === 'Low' ? 'text-yellow-500' : 'text-lime'}`}>
                  Inventory: {supply.status}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12 text-center">
        <p className="text-[10px] font-mono text-clay-dark uppercase tracking-[0.3em]">
          Made by LingLink AI and DigLab AI
        </p>
      </div>
    </motion.div>
  );
}